import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { typeTokens, shapeTokens } from "../../../theme/tokens";

/**
 * The booking pipeline, coloured with the app's own status ramp.
 *
 * Each stage uses the same colour the app uses for that state, so the landing
 * page teaches the colour language a user will see inside the product: a
 * status-coloured bar across the top of a panel is the core pattern.
 */
export default function BookingPipeline(): React.ReactElement {
  const { palette } = useTheme();

  const stages = [
    {
      label: "Hold",
      accent: palette.status.neutral,
      description: "Take 1st, 2nd, or 3rd position on a date. Drag to hold a run.",
    },
    {
      label: "Challenge",
      accent: palette.status.attention,
      description: "A timed window forces a decision instead of letting dates sit.",
    },
    {
      label: "Offer",
      accent: palette.status.inFlight,
      description: "Send a door deal or guarantee and track the response.",
    },
    {
      label: "Advanced",
      accent: palette.status.ready,
      description: "Riders, stage plots, and input lists collected before load-in.",
    },
    {
      label: "Settled",
      accent: palette.status.settled,
      description: "Split point, overage, and house nut reconciled and signed off.",
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        sx={{ ...typeTokens.microLabel, color: "text.secondary", mb: 3 }}
      >
        One date, end to end
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(5, 1fr)",
          },
        }}
      >
        {stages.map(({ label, accent, description }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{
              height: "100%",
              // The status bar is the signature pattern from the walkthrough.
              borderTop: `${shapeTokens.accentBarHeight}px solid ${accent}`,
              p: 2.5,
            }}
          >
            <Typography sx={{ ...typeTokens.statusChip, color: accent, mb: 1 }}>
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
